import React from 'react';
import { ShieldCheck, Mail } from 'lucide-react';

const PrivacyPage: React.FC = () => {
  return (
    <div className="pt-10 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-12 animate-fade-in">
             <div className="inline-flex items-center justify-center p-3 bg-mira-accent/10 rounded-xl mb-4 border border-mira-accent/20 shadow-[0_0_15px_rgba(26,140,255,0.2)]">
              <ShieldCheck className="w-8 h-8 text-mira-accent" />
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 text-white tracking-tight drop-shadow-[0_0_15px_rgba(26,140,255,0.3)]">
            Aviso de <span className="text-mira-accent">Privacidad</span>
            </h1>
            <p className="text-gray-400">Última actualización: {new Date().toLocaleDateString()}</p>
        </div>
        
        <div className="bg-mira-surface border border-mira-border rounded-2xl p-8 md:p-12 shadow-2xl animate-fade-up">
            <div className="prose prose-invert prose-blue max-w-none text-gray-300 space-y-8">
                
                <p className="text-lg leading-relaxed">
                    En <strong>Mira-IA</strong>, somos responsables del tratamiento, uso y protección de tus datos personales. Valoramos tu confianza y por ello te informamos cómo recopilamos, utilizamos y salvaguardamos tu información conforme a lo establecido en la <strong>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</strong>.
                </p>

                {/* Sección 1 */}
                <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 1.</span> Datos personales que recopilamos
                    </h3>
                    <p className="mb-4">
                        Cuando solicitas información, cotizaciones, soporte o utilizas nuestros servicios de chatbots y automatización, podemos solicitar los siguientes datos:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 marker:text-mira-accent">
                        <li>Nombre completo</li>
                        <li>Nombre del negocio</li>
                        <li>Correo electrónico</li>
                        <li>Número de teléfono</li>
                        <li>Información relacionada con tu empresa o requerimientos de automatización</li>
                        <li>Mensajes enviados mediante nuestros formularios o canales de contacto</li>
                    </ul>
                    <p className="mt-4 text-sm bg-blue-900/20 p-3 rounded-lg border border-blue-500/20 text-blue-200">
                        No recopilamos datos sensibles a menos que sean estrictamente necesarios para un servicio específico.
                    </p>
                </section>

                {/* Sección 2 */}
                <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 2.</span> Finalidades del tratamiento de tus datos
                    </h3>
                    <p className="mb-4">Tu información será utilizada para:</p>
                    <ul className="list-disc pl-6 space-y-2 marker:text-mira-accent">
                        <li>Brindarte información, cotizaciones, demos y soporte técnico.</li>
                        <li>Desarrollar, configurar e implementar soluciones de automatización o chatbots.</li>
                        <li>Dar seguimiento a tus solicitudes o proyectos.</li>
                        <li>Mejorar nuestros servicios y experiencia del usuario.</li>
                        <li>Enviar avisos, recordatorios o mensajes relacionados con procesos técnicos o contratados.</li>
                        <li>Fines mercadotécnicos o publicitarios (solo con tu consentimiento previo).</li>
                    </ul>
                    <p className="mt-4 font-semibold text-white">
                        No utilizaremos tu información para finalidades distintas sin tu autorización explícita.
                    </p>
                </section>

                {/* Sección 3 */}
                <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 3.</span> Transferencia de datos
                    </h3>
                    <p className="mb-4">Tus datos no serán compartidos con terceros, excepto:</p>
                    <ul className="list-disc pl-6 space-y-2 marker:text-mira-accent">
                        <li>Proveedores tecnológicos necesarios para la implementación del servicio (API de Meta, servicios de automatización, servidores, etc.).</li>
                        <li>Autoridades competentes en caso de requerimiento legal.</li>
                    </ul>
                    <p className="mt-4">
                        En todos los casos, nos aseguramos de que los terceros cumplan con estándares adecuados de privacidad.
                    </p>
                </section>

                {/* Sección 4 */}
                <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 4.</span> Protección de tu información
                    </h3>
                    <p className="mb-4">
                        Implementamos medidas físicas, técnicas y administrativas para proteger tus datos contra:
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        {['Accesos no autorizados', 'Uso indebido', 'Pérdida', 'Modificación', 'Divulgación'].map((item, i) => (
                            <div key={i} className="bg-mira-bg p-2 rounded text-center text-sm border border-mira-border text-gray-400">
                                {item}
                            </div>
                        ))}
                    </div>
                    <p>Tu información es almacenada en servidores seguros y plataformas confiables.</p>
                </section>

                {/* Sección 5 */}
                <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 5.</span> Derechos ARCO
                    </h3>
                    <p className="mb-4">Tienes derecho a:</p>
                    <ul className="list-disc pl-6 space-y-2 marker:text-mira-accent mb-6">
                        <li>Acceder a tus datos personales</li>
                        <li>Rectificarlos si son incorrectos o incompletos</li>
                        <li>Cancelarlos cuando lo desees</li>
                        <li>Oponerte al uso de tus datos</li>
                    </ul>
                    <div className="bg-mira-accent/5 border border-mira-accent/20 p-6 rounded-xl">
                        <p className="mb-2 font-bold text-white">Para ejercer estos derechos, envía una solicitud al correo:</p>
                        <a href="mailto:diegorioia25@gmail.com" className="flex items-center gap-2 text-mira-accent font-bold hover:text-white transition-colors text-lg">
                            <Mail className="w-5 h-5" /> diegorioia25@gmail.com
                        </a>
                        <p className="mt-3 text-sm text-gray-400">
                            Deberás incluir tu nombre completo, medio de contacto y la descripción de la solicitud.
                        </p>
                    </div>
                </section>

                 {/* Sección 6 */}
                 <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 6.</span> Revocación del consentimiento
                    </h3>
                    <p className="mb-4">
                        Puedes revocar en cualquier momento el consentimiento otorgado para el uso de tus datos enviando un correo a:
                    </p>
                    <a href="mailto:diegorioia25@gmail.com" className="text-mira-accent font-bold hover:underline">
                        diegorioia25@gmail.com
                    </a>
                    <p className="mt-2">
                        Te informaremos sobre el proceso, tiempo de respuesta y estatus.
                    </p>
                </section>

                {/* Sección 7 */}
                <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 7.</span> Uso de cookies y tecnologías similares
                    </h3>
                    <p>
                        Nuestro sitio puede utilizar cookies y herramientas de análisis para mejorar la experiencia del usuario. 
                        Puedes desactivarlas desde la configuración de tu navegador.
                    </p>
                </section>

                {/* Sección 8 */}
                <section>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 8.</span> Modificaciones al Aviso de Privacidad
                    </h3>
                    <p>
                        Este aviso puede ser actualizado para cumplir con cambios legales o mejoras en nuestros procesos. 
                        La fecha de la última actualización siempre estará publicada en esta página.
                    </p>
                </section>

                {/* Sección 9 */}
                <section className="border-t border-mira-border pt-8 mt-8">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <span className="text-mira-accent">🔷 9.</span> Contacto
                    </h3>
                    <p className="mb-4">
                        Para dudas o aclaraciones relacionadas con privacidad de datos:
                    </p>
                    <a href="mailto:diegorioia25@gmail.com" className="inline-flex items-center gap-2 px-6 py-3 bg-mira-bg border border-mira-border rounded-lg text-white hover:border-mira-accent hover:text-mira-accent transition-all">
                        <Mail className="w-5 h-5" /> diegorioia25@gmail.com
                    </a>
                </section>

            </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;